/**
 * Products Admin API Service
 * Manages system-wide product operations for admin
 */

import api from './api';
import type { Product } from '@/types';

export interface AdminProduct extends Product {
  orgId: string;
  orgName?: string;
}

class ProductsService {
  private baseUrl = '/products';

  /**
   * List all products system-wide (admin view)
   */
  async listAllProducts(): Promise<AdminProduct[]> {
    const response = await api.get(`${this.baseUrl}/all`);
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Failed to fetch products (${response.status}): ${errorText || response.statusText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  /**
   * Get product by ID
   */
  async getProduct(id: string): Promise<AdminProduct> {
    const response = await api.get(`${this.baseUrl}/${id}`);
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Product not found');
      }
      throw new Error('Failed to fetch product');
    }
    return response.json();
  }

  /**
   * Delete a product
   */
  async deleteProduct(id: string): Promise<void> {
    const response = await api.delete(`${this.baseUrl}/${id}`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to delete product');
    }
  }
}

export const productsService = new ProductsService();
